import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { Plus, Minus } from "lucide-react";

const linkClass =
  "text-brand-dark font-semibold underline decoration-brand/60 underline-offset-4 hover:decoration-brand-dark transition-colors";

const emailLink = (
  <a href="mailto:vreau@isic.ro" className={linkClass} data-testid="faq-email-link">
    vreau@isic.ro
  </a>
);

const FAQ_ITEMS = [
  {
    q: "Reducerile se aplică automat atunci când plătesc cu BRD-ISIC?",
    a: (
      <p>
        Nu în toate cazurile. La partenerii fizici, prezintă cardul BRD-ISIC sau
        legitimația digitală din Alive App înainte de comandă sau de efectuarea
        plății. Pentru ofertele online, urmează instrucțiunile afișate în pagina
        reducerii, deoarece poate fi necesar să folosești un link dedicat, un
        cod promoțional sau o metodă de verificare a statutului de student.
      </p>
    ),
  },
  {
    q: "Unde găsesc reducerile disponibile și condițiile lor?",
    a: (
      <>
        <p>
          Poți descoperi toate reducerile pe isic.ro și în Alive App. Verifică
          întotdeauna condițiile fiecărei oferte, perioada de valabilitate,
          locațiile participante și modul în care trebuie accesată reducerea.
        </p>
        <p className="mt-4">
          <a
            href="https://www.isic.ro/reduceri"
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
            data-testid="faq-discounts-link"
          >
            Vezi toate reducerile
          </a>
        </p>
      </>
    ),
  },
  {
    q: "Cum îmi găsesc legitimația BRD-ISIC în Alive App?",
    a: (
      <>
        <p>
          Descarcă Alive App și creează-ți contul folosind exact datele
          comunicate în emailul de bun venit primit din partea ISIC. După
          crearea contului, aplicația ar trebui să identifice automat
          legitimația ta BRD-ISIC.
        </p>
        <p className="mt-4">
          <a
            href="https://www.isic.ro/mobile-app"
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
            data-testid="faq-alive-app-link"
          >
            Descarcă Alive App
          </a>
        </p>
      </>
    ),
  },
  {
    q: "Ce fac dacă legitimația nu apare automat în aplicație?",
    a: (
      <>
        <p>
          Verifică dacă ai introdus numele, prenumele și data nașterii{" "}
          <strong className="font-semibold text-zinc-900">
            exact în forma comunicată
          </strong>{" "}
          în emailul primit din partea ISIC, inclusiv eventualele cratime sau
          spații.
        </p>
        <p className="mt-4">
          Dacă legitimația tot nu apare, adaug-o manual folosind seria ISIC
          tipărită pe cardul BRD-ISIC, cea care începe cu{" "}
          <strong className="font-semibold text-zinc-900">S04-0</strong>.
        </p>
        <p className="mt-4">
          Dacă întâmpini în continuare probleme, scrie-ne la {emailLink} și te
          ajutăm.
        </p>
      </>
    ),
  },
  {
    q: "Pot folosi legitimația digitală în locul cardului fizic?",
    a: (
      <p>
        Da, legitimația digitală disponibilă în Alive App îți permite să
        demonstrezi statutul ISIC și să accesezi beneficiile direct de pe
        telefon. Totuși, verifică instrucțiunile fiecărei oferte, deoarece modul
        de validare poate diferi de la un partener la altul.
      </p>
    ),
  },
  {
    q: "Pot combina reducerea ISIC cu alte promoții?",
    a: (
      <p>
        Depinde de condițiile stabilite de fiecare partener. Unele reduceri pot
        fi cumulate cu alte oferte, iar altele nu. Verifică întotdeauna
        condițiile beneficiului înainte să îl folosești sau întreabă direct în
        locația partenerului.
      </p>
    ),
  },
  {
    q: "Cui mă adresez dacă am nevoie de ajutor?",
    a: (
      <>
        <p>
          Pentru probleme legate de contul bancar, plăți, PIN, blocarea,
          pierderea sau înlocuirea cardului, contactează BRD.
        </p>
        <p className="mt-4">
          Pentru reducerile ISIC, legitimația digitală, Alive App sau
          identificarea legitimației tale, contactează echipa ISIC la{" "}
          {emailLink}.
        </p>
      </>
    ),
  },
];

export const FAQ = () => (
  <section
    id="faq"
    className="bg-zinc-50 border-y border-zinc-200 py-16 md:py-24"
    data-testid="faq-section"
  >
    <div className="max-w-3xl mx-auto px-5 md:px-8">
      <h2
        className="font-heading font-black text-3xl md:text-4xl tracking-tight text-zinc-900"
        data-testid="faq-title"
      >
        Cum folosești BRD-ISIC, în practică
      </h2>
      <p
        className="mt-4 text-base md:text-lg text-zinc-600 leading-relaxed"
        data-testid="faq-subtitle"
      >
        Tot ce trebuie să știi despre reduceri, Alive App și situațiile în care
        ai nevoie de ajutor.
      </p>

      <AccordionPrimitive.Root
        type="single"
        collapsible
        className="mt-10 divide-y divide-zinc-200 border-y border-zinc-200"
      >
        {FAQ_ITEMS.map((item, i) => (
          <AccordionPrimitive.Item
            key={i}
            value={`item-${i}`}
            className="group"
          >
            <AccordionPrimitive.Header className="flex">
              <AccordionPrimitive.Trigger
                data-testid={`faq-trigger-${i}`}
                className="flex flex-1 items-start justify-between gap-4 py-5 md:py-6 text-left font-heading font-semibold text-base md:text-lg text-zinc-900 hover:text-brand-dark transition-colors [&[data-state=open]>.faq-icon-plus]:hidden [&[data-state=closed]>.faq-icon-minus]:hidden"
              >
                <span>{item.q}</span>
                <Plus
                  className="faq-icon-plus h-5 w-5 shrink-0 mt-1 text-zinc-500 transition-colors group-hover:text-brand-dark"
                  aria-hidden="true"
                />
                <Minus
                  className="faq-icon-minus h-5 w-5 shrink-0 mt-1 text-brand-dark"
                  aria-hidden="true"
                />
              </AccordionPrimitive.Trigger>
            </AccordionPrimitive.Header>
            <AccordionPrimitive.Content
              data-testid={`faq-content-${i}`}
              className="overflow-hidden text-sm md:text-base text-zinc-600 leading-relaxed data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
            >
              <div className="pb-6 pr-8">{item.a}</div>
            </AccordionPrimitive.Content>
          </AccordionPrimitive.Item>
        ))}
      </AccordionPrimitive.Root>
    </div>
  </section>
);
